/*
 * The console overlay's own behaviour, kept apart from the stream and input
 * code in desktop.ts: it only reads what desktop.ts writes into the DOM.
 *
 * - The chip mirrors the frame rate next to the status while the picture is
 *   live, so the panel does not have to be open to see it.
 * - "Hide" folds the overlay to a dot in the corner (remembered in this
 *   browser); selecting the dot brings it back.
 * - Closing the panel hands the keyboard back to the desktop, so typing goes
 *   to the guest again without another click on the picture.
 */

const HIDDEN_KEY = "kiste.viewer.overlay";

const consoleElement = document.querySelector<HTMLDetailsElement>("details.console");
const chipFps = document.querySelector<HTMLElement>("#chip-fps");
const statFps = document.querySelector<HTMLElement>("#stat-fps");
const hideButton = document.querySelector<HTMLButtonElement>("#chrome-hide");
const viewport = document.querySelector<HTMLElement>("#viewport");
const surface = document.querySelector<HTMLVideoElement>("#surface");

function readHidden(): boolean {
  try {
    return localStorage.getItem(HIDDEN_KEY) === "hidden";
  } catch {
    return false;
  }
}

function setHidden(hidden: boolean): void {
  if (!consoleElement) return;
  consoleElement.dataset.hidden = String(hidden);
  try {
    localStorage.setItem(HIDDEN_KEY, hidden ? "hidden" : "shown");
  } catch {
    // Storage can be refused; the choice then lasts this page.
  }
}

function syncFps(): void {
  if (!chipFps || !statFps) return;
  const live = viewport?.dataset.live === "true";
  const value = statFps.textContent?.trim() ?? "";
  chipFps.textContent = !live || value === "" || value === "—" ? "" : /^\d+$/.test(value) ? `${value} fps` : value.toLowerCase();
}

function returnFocus(): void {
  if (surface && !surface.hidden) {
    surface.focus({ preventScroll: true });
  } else {
    viewport?.focus({ preventScroll: true });
  }
}

if (consoleElement) {
  setHidden(readHidden());
  hideButton?.addEventListener("click", () => {
    setHidden(true);
    consoleElement.open = false;
  });
  consoleElement.addEventListener("toggle", () => {
    if (consoleElement.open) {
      if (consoleElement.dataset.hidden === "true") setHidden(false);
      return;
    }
    returnFocus();
  });
}

if (statFps && viewport) {
  const observer = new MutationObserver(syncFps);
  observer.observe(statFps, { childList: true, characterData: true, subtree: true });
  observer.observe(viewport, { attributes: true, attributeFilter: ["data-live"] });
  syncFps();
}
